import { dictionaryApi } from './dictionaryClient';
import { normalizeGana, type Gana } from './ganaApi';

export type BartikPadachhedGanaLink = {
  id: number;
  ashtadhyayiBartikPadachhedId: number;
  ganaId: number;
  gana: Gana | null;
};

export type CreateBartikPadachhedGanaLinkBody = {
  ashtadhyayiBartikPadachhedId: number;
  ganaId: number;
};

export type UpdateBartikPadachhedGanaLinkBody = {
  ganaId?: number;
};

function isRecord(x: unknown): x is Record<string, unknown> {
  return x != null && typeof x === 'object' && !Array.isArray(x);
}

function unwrapEntityPayload(raw: unknown): unknown {
  if (isRecord(raw) && 'data' in raw) return raw.data;
  return raw;
}

function num(o: Record<string, unknown>, key: string): number | null {
  const v = o[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}

export function normalizeBartikPadachhedGanaLink(raw: unknown): BartikPadachhedGanaLink | null {
  if (!isRecord(raw)) return null;
  const id = num(raw, 'id');
  const ashtadhyayiBartikPadachhedId =
    num(raw, 'ashtadhyayiBartikPadachhedId') ?? num(raw, 'ashtadhyayi_bartik_padachhed_id');
  const ganaId = num(raw, 'ganaId') ?? num(raw, 'gana_id');
  if (id == null || ashtadhyayiBartikPadachhedId == null || ganaId == null) return null;

  const ganaRaw = raw.gana;
  const gana = ganaRaw ? normalizeGana(ganaRaw) : null;

  return {
    id,
    ashtadhyayiBartikPadachhedId,
    ganaId,
    gana,
  };
}

function normalizeList(data: unknown): BartikPadachhedGanaLink[] {
  const rows =
    Array.isArray(data) ? data : isRecord(data) && Array.isArray(data.data) ? (data.data as unknown[]) : [];
  return rows.map(normalizeBartikPadachhedGanaLink).filter((x): x is BartikPadachhedGanaLink => x != null);
}

export async function listBartikPadachhedGanaLinks(filters?: {
  ashtadhyayiBartikPadachhedId?: number;
  ganaId?: number;
}): Promise<BartikPadachhedGanaLink[]> {
  const params: Record<string, number> = {};
  if (filters?.ashtadhyayiBartikPadachhedId != null) {
    params.ashtadhyayiBartikPadachhedId = filters.ashtadhyayiBartikPadachhedId;
  }
  if (filters?.ganaId != null) {
    params.ganaId = filters.ganaId;
  }
  const res = await dictionaryApi.get<unknown>('/ashtadhyayi-bartik-padachhed-gana', {
    params: Object.keys(params).length > 0 ? params : undefined,
  });
  return normalizeList(res.data);
}

export async function getBartikPadachhedGanaLink(id: number): Promise<BartikPadachhedGanaLink> {
  const res = await dictionaryApi.get<unknown>(`/ashtadhyayi-bartik-padachhed-gana/${id}`);
  const row = normalizeBartikPadachhedGanaLink(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid bartik padachhed-gana link response');
  return row;
}

export async function createBartikPadachhedGanaLink(
  body: CreateBartikPadachhedGanaLinkBody
): Promise<BartikPadachhedGanaLink> {
  const res = await dictionaryApi.post<unknown>('/ashtadhyayi-bartik-padachhed-gana', body);
  const row = normalizeBartikPadachhedGanaLink(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid bartik padachhed-gana link response');
  return row;
}

export async function updateBartikPadachhedGanaLink(
  id: number,
  body: UpdateBartikPadachhedGanaLinkBody
): Promise<BartikPadachhedGanaLink> {
  const res = await dictionaryApi.patch<unknown>(`/ashtadhyayi-bartik-padachhed-gana/${id}`, body);
  const row = normalizeBartikPadachhedGanaLink(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid bartik padachhed-gana link response');
  return row;
}

export async function deleteBartikPadachhedGanaLink(id: number): Promise<void> {
  await dictionaryApi.delete(`/ashtadhyayi-bartik-padachhed-gana/${id}`);
}
